/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { CoverImageUpload } from '../../container/admin/subscriptions/components/shared/SharedComponents';
import ReactDOM from 'react-dom';
import { Checkbox, CircularProgress } from '@mui/material';
import subscriptionServices from '../../services/subscriptionServices';
import {
  Search, BookOpen, Trash2, MoreVertical, Eye, Upload, PlusCircle, X,
  SlidersHorizontal, ArrowUpDown, RotateCcw, AlertCircle, Pencil,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import Page from '../../components/Page';
import ResponsiveTable from '../../components/Table/Responsivetable';
import DeleteAlert from '../../components/modal/DeleteModal';
import SnackBar from '../../components/snackbar/snackbar';
import adminServices from '../../services/adminServices';
import useDebounce from '../../hooks/useDebounce';

const SUBJECTS     = ['Biology','Physics','Chemistry','Mathematics','History','Geography','Computer Science','English','Economics'];
const CLASS_LEVELS = ['Class 6','Class 7','Class 8','Class 9','Class 10','Class 11','Class 12','Undergraduate'];
const LANGUAGES    = ['English','Hindi','Tamil','Telugu','Kannada','Malayalam','Marathi','Bengali'];
const BOARDS       = ['CBSE','ICSE','State Board','IB','Cambridge','Other'];

const INPUT_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400';

// Must match MAX_EBOOK_IMPORT_SIZE on the API. Checked here as well so an author
// with a 200 MB file learns immediately instead of after the upload.
const MAX_DOC_MB = 50;
const DOCX_MIME  = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// The import runs inside the upload request, so the dialog reports the upload's
// own progress rather than polling a job.

// The chrome shared by every step of the create dialog. Defined at module scope
// so switching steps does not remount the dialog itself.
function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          <h3 className="text-base font-bold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateEbookModal({ onClose, onCreated }) {
  // 'mode'   - choose how this eBook gets its content
  // 'form'   - the metadata form (plus the document dropzone in upload mode)
  // 'result' - what the import produced, or why it failed
  const [step, setStep] = useState('mode');
  const [mode, setMode] = useState(null); // 'manual' | 'upload'
  const [docFile, setDocFile] = useState(null);
  const [docError, setDocError] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [job, setJob] = useState(null);
  const [docDragOver, setDocDragOver] = useState(false);
  const docInputRef = useRef(null);

  const [form, setForm] = useState({ title: '', subject: '', classLevel: '', language: 'English', board: '', description: '', examTypeId: '' });
  // eBook "series" — an assessment type (NEET / JEE / …), same list as Test Series.
  const [seriesTypes, setSeriesTypes] = useState([]);
  const [seriesSubjects, setSeriesSubjects] = useState(SUBJECTS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverImgPreview, setCoverImgPreview] = useState(null);
  const [coverImageError, setCoverImageError] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [contentTab, setContentTab] = useState('ebooks');
  const [ebooks, setEbooks] = useState([]);
  const [courses, setCourses] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [ebookSearch, setEbookSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [assessmentSearch, setAssessmentSearch] = useState('');
  const [selectedEbooks, setSelectedEbooks] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  // Assessment chapters this book covers. Stored on ebooks.related_chapter_id —
  // the same id space questions.chapter_id[] uses — so a wrong answer can find
  // the book that teaches it.
  const [chapters, setChapters] = useState([]);
  const [chapterSearch, setChapterSearch] = useState('');
  const [selectedChapters, setSelectedChapters] = useState([]);

  useEffect(() => {
    fetchContent();
    adminServices.getAssessmentTypeDropdown()
      .then((res) => {
        const rows = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(rows)) setSeriesTypes(rows.filter((t) => t.is_active));
      })
      .catch((e) => console.error('Failed to load assessment types', e));
  }, []);

  // Subjects for the chosen series come from configuration
  // (/assessment/assessment-subject); fall back to the static list otherwise.
  useEffect(() => {
    let cancelled = false;
    if (!form.examTypeId) { setSeriesSubjects(SUBJECTS); return undefined; }
    adminServices.getAssessmentSubject(form.examTypeId)
      .then((res) => {
        const rows = res?.data?.data ?? res?.data ?? [];
        const names = Array.isArray(rows) ? rows.map((r) => r.name).filter(Boolean) : [];
        if (!cancelled) setSeriesSubjects(names.length ? names : SUBJECTS);
      })
      .catch(() => { if (!cancelled) setSeriesSubjects(SUBJECTS); });
    return () => { cancelled = true; };
  }, [form.examTypeId]);

  const fetchContent = async () => {
    setContentLoading(true);
    try {
      const [ebookRes, courseRes, assessmentRes, chapterRes] = await Promise.all([
        adminServices.getEbooks('', 1, 500),
        adminServices.getLookUpData(['course']),
        adminServices.getGenericAssessmentDropDown({ page: 1, pageSize: 500, audience: 'B2C' }),
        adminServices.getAssessmentChaptersForEbook('', 500),
      ]);
      if (chapterRes.ok) setChapters(Array.isArray(chapterRes.data) ? chapterRes.data : []);
      if (ebookRes.ok) {
        const rows = Array.isArray(ebookRes.data) ? ebookRes.data : ebookRes.data?.rows || [];
        setEbooks(rows);
      }
      if (courseRes.ok) {
        setCourses((courseRes.data.course || []).filter((c) => {
          const code = (c.audienceCode || '').toUpperCase();
          return Number(c.status) === 1 && (code === 'B2C' || code === 'BOTH');
        }));
      }
      if (assessmentRes.ok) {
        const rows = Array.isArray(assessmentRes.data?.rows) ? assessmentRes.data.rows : assessmentRes.data || [];
        setAssessments(rows.filter((a) => a.audience === 'B2C' || a.audience === 'BOTH'));
      }
    } catch (e) { console.error(e); }
    setContentLoading(false);
  };

  const imageWidthAndHeight = (provideFile) => {
    if (!provideFile) return Promise.resolve({ width: null, height: null });
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.src = reader.result;
      };
      reader.readAsDataURL(provideFile);
    });
  };

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    setCoverImage(null); setCoverImgPreview(null); setCoverImageError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/bmp'].includes(file.type)) { setCoverImageError('Only JPG, PNG, BMP allowed'); return; }
    if (file.size > 104857600) { setCoverImageError('Max file size is 100 MB'); return; }
    imageWidthAndHeight(file).then((res) => {
      if (res.width >= 1200 && res.height >= 400) { setCoverImage(file); setCoverImgPreview(URL.createObjectURL(file)); }
      else setCoverImageError('Image must be at least 1200 × 400 px');
    });
  };

  const toggleId = (list, setList, id) => {
    setList((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // One entry per Content tab. A fourth branch made the inline ternaries
  // unreadable, so each tab now declares its own search box and selection.
  const SEARCH_OF = {
    ebooks:      { value: ebookSearch,      set: setEbookSearch },
    courses:     { value: courseSearch,     set: setCourseSearch },
    assessments: { value: assessmentSearch, set: setAssessmentSearch },
    chapters:    { value: chapterSearch,    set: setChapterSearch },
  };
  const SELECTION_OF = {
    ebooks:      { list: selectedEbooks,      set: setSelectedEbooks },
    courses:     { list: selectedCourses,     set: setSelectedCourses },
    assessments: { list: selectedAssessments, set: setSelectedAssessments },
    chapters:    { list: selectedChapters,    set: setSelectedChapters },
  };

  const pickDocument = (file) => {
    setDocFile(null); setDocError('');
    if (!file) return;
    const isDocx = file.name.toLowerCase().endsWith('.docx')
      && (!file.type || file.type === DOCX_MIME || file.type === 'application/octet-stream');
    if (file.name.toLowerCase().endsWith('.doc')) {
      setDocError('Legacy .doc files are not supported. Save the file as .docx in Word and try again.');
      return;
    }
    if (!isDocx) { setDocError('Only Word documents (.docx) can be uploaded.'); return; }
    if (file.size > MAX_DOC_MB * 1024 * 1024) { setDocError(`Document must be under ${MAX_DOC_MB} MB.`); return; }
    setDocFile(file);
  };

  const handleSubmit = async () => {
    if (!form.title.trim())  return setError('Title is required.');
    if (mode === 'upload' && !docFile) return setDocError('Please choose a Word document to upload.');
    setError('');
    setSaving(true);
    try {
      const relatedContent = {
        courses: selectedCourses.map(id => {
          const c = courses.find(c => c.id === id);
          return { id, name: c?.title || '' };
        }),
        assessments: selectedAssessments.map(id => {
          const a = assessments.find(a => a.id === id);
          return { id, name: a?.title || '' };
        }),
        ebooks: selectedEbooks.map(id => {
          const e = ebooks.find(e => e.id === id);
          return { id, name: e?.title || '' };
        }),
      };

      const fd = new FormData();
      fd.append('title', form.title.trim());
      if (form.subject) fd.append('subjectId', form.subject);
      if (form.classLevel) fd.append('classLevel', form.classLevel);
      if (form.board) fd.append('board', form.board);
      fd.append('language', form.language || 'English');
      if (form.description.trim()) fd.append('description', form.description.trim());
      if (shortDescription.trim()) fd.append('shortDescription', shortDescription.trim());
      fd.append('relatedContent', JSON.stringify(relatedContent));
      fd.append('relatedChapterId', JSON.stringify(selectedChapters));
      if (form.examTypeId) fd.append('examTypeId', form.examTypeId);
      if (coverImage instanceof File) fd.append('coverImage', coverImage);

      const res = await adminServices.createEbook(fd);

      const newId = res?.data?.id;
      if (!newId) { setError('Created but could not retrieve ID. Please refresh the list.'); setSaving(false); return; }

      // Manual authoring is unchanged: straight into the editor.
      if (mode !== 'upload') { onCreated(newId); return; }

      // Upload mode: the eBook shell exists, now send the document to be parsed.
      // The eBook is deliberately left in place if this fails, so the author can
      // retry the upload or fall back to writing it by hand.
      const failImport = (message) => {
        setJob({ ebookId: newId, status: 'FAILED', errorMessage: message });
        setStep('result');
      };

      let importRes;
      // Small documents post straight to the API (simplest path, no S3 round
      // trip). Anything near the 10 MB API-gateway limit goes to S3 first via a
      // presigned URL - the API request then only carries the object key, and
      // the server deletes the .docx as soon as the parse finishes.
      const DIRECT_UPLOAD_LIMIT = 9 * 1024 * 1024;
      if (docFile.size <= DIRECT_UPLOAD_LIMIT) {
        const docForm = new FormData();
        docForm.append('document', docFile);
        importRes = await adminServices.importEbookDocument(newId, docForm, (evt) => {
          if (evt?.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        });
      } else {
       
        setUploadPct(1);
        const urlRes = await adminServices.getEbookImportUploadUrl(newId, docFile.name);
        if (!urlRes.ok || !urlRes.data?.url) {
          return failImport(urlRes?.data?.message || 'Could not start the upload.');
        }
        const s3Res = await adminServices.putFileToUrl(urlRes.data.url, docFile, (evt) => {
          if (evt?.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        });
        if (!s3Res.ok) {
          return failImport('The document upload failed. Please try again.');
        }
        setUploadPct(100);
        importRes = await adminServices.importEbookDocumentByKey(newId, {
          objectKey: urlRes.data.key,
          fileName: docFile.name,
          fileSize: docFile.size,
        });
      }

      if (!importRes.ok) {
        return failImport(importRes?.data?.message || 'The document could not be imported.');
      }
      setJob({ ...importRes.data, ebookId: newId, status: 'COMPLETED' });
      setStep('result');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create eBook.');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 1: how should this eBook get its content? ─────────────────────────
  if (step === 'mode') {
    const OPTIONS = [
      {
        key: 'manual',
        icon: <PlusCircle className="w-6 h-6" />,
        title: 'Build it manually',
        blurb: 'Open the editor and write chapters and topics yourself, with the full formatting toolbar.',
        // Tailwind only emits classes it can see as complete strings in the
        // source, so these cannot be assembled from an accent name at runtime.
        card: 'hover:border-indigo-500 hover:bg-indigo-50',
        badge: 'bg-indigo-100 text-indigo-600',
      },
      {
        key: 'upload',
        icon: <Upload className="w-6 h-6" />,
        title: 'Upload a document',
        blurb: 'We read a Word (.docx) file and build the chapters, images, tables and equations for you. You review before publishing.',
        card: 'hover:border-emerald-500 hover:bg-emerald-50',
        badge: 'bg-emerald-100 text-emerald-600',
      },
    ];
    return (
      <ModalShell title="New eBook" onClose={onClose}>
        <div className="p-6 flex flex-col gap-3 overflow-y-auto">
          <p className="text-sm text-gray-500">How would you like to create this eBook?</p>
          {OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => { setMode(opt.key); setStep('form'); }}
              className={`flex items-start gap-4 text-left p-4 rounded-xl border-2 border-gray-200 transition-colors group ${opt.card}`}
            >
              <span className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${opt.badge}`}>
                {opt.icon}
              </span>
              <span className="flex flex-col gap-1">
                <span className="text-sm font-bold text-gray-900">{opt.title}</span>
                <span className="text-xs text-gray-500 leading-relaxed">{opt.blurb}</span>
              </span>
            </button>
          ))}
        </div>
        <div className="flex gap-3 px-6 pb-5 shrink-0 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </ModalShell>
    );
  }

  // ── Step 3: the outcome of the import.
  if (step === 'result') {
    const failed = job?.status === 'FAILED';
    const done   = job?.status === 'COMPLETED';
    return (
      <ModalShell title={failed ? 'Import failed' : 'Document imported'} onClose={onClose}>
        <div className="p-6 flex flex-col gap-4 overflow-y-auto">
          <div className={`flex items-start gap-3 p-4 rounded-xl ${failed ? 'bg-red-50' : done ? 'bg-emerald-50' : 'bg-indigo-50'}`}>
            <span className="shrink-0 mt-0.5">
              {failed ? <X className="w-5 h-5 text-red-600" /> : <Eye className="w-5 h-5 text-emerald-600" />}
            </span>
            <div className="flex flex-col gap-1">
              <p className={`text-sm font-bold ${failed ? 'text-red-800' : 'text-emerald-800'}`}>
                {failed ? 'Import failed' : 'Ready to review'}
              </p>
              <p className={`text-xs leading-relaxed ${failed ? 'text-red-700' : 'text-emerald-700'}`}>
                {failed
                  ? (job?.errorMessage || 'The document could not be read.')
                  : 'Open the eBook to review the content, edit anything you want, then publish it.'}
              </p>
            </div>
          </div>

          {done && job?.stats && (
            <div className="grid grid-cols-3 gap-2">
              {[
                ['Chapters', job.stats.chapters], ['Topics', job.stats.topics],
                ['Images', job.stats.images], ['Equations', job.stats.equations],
                ['Tables', job.stats.tables],
              ].filter(([, n]) => n > 0).map(([label, n]) => (
                <div key={label} className="rounded-lg bg-gray-50 px-3 py-2 text-center">
                  <p className="text-base font-bold text-gray-900">{n}</p>
                  <p className="text-[11px] text-gray-500">{label}</p>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400">{docFile?.name}</p>
        </div>
        <div className="flex gap-3 px-6 pb-5 shrink-0 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => onCreated(job.ebookId)}
            className="flex-1 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700"
          >
            {done ? 'Review now' : 'Open eBook'}
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <BookOpen className="w-5 h-5 text-indigo-600" />
          <h3 className="text-base font-bold text-gray-900">New eBook</h3>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-2 gap-4 overflow-y-auto flex-1 min-h-0">
          {mode === 'upload' && (
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Word document *</span>
              <div
                onDragOver={(e) => { e.preventDefault(); setDocDragOver(true); }}
                onDragLeave={() => setDocDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDocDragOver(false); pickDocument(e.dataTransfer.files?.[0]); }}
                onClick={() => docInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
                  docDragOver ? 'border-emerald-500 bg-emerald-50'
                    : docFile ? 'border-emerald-400 bg-emerald-50/50'
                    : 'border-gray-300 bg-gray-50 hover:border-emerald-400'}`}
              >
                <input
                  ref={docInputRef}
                  type="file"
                  className="hidden"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => pickDocument(e.target.files?.[0])}
                />
                {docFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <Upload className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-800">{docFile.name}</span>
                    <span className="text-xs text-emerald-600">({(docFile.size / (1024 * 1024)).toFixed(1)} MB)</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1.5" />
                    <p className="text-sm text-gray-600 font-medium">Drag &amp; drop your .docx, or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">Word documents only, up to {MAX_DOC_MB} MB</p>
                  </>
                )}
              </div>
              {docError && <p className="text-xs text-red-600">{docError}</p>}
              <p className="text-xs text-gray-400 leading-relaxed">
                Headings become chapters and topics. Images, tables, equations and formatting
                are carried across, and you can edit everything before publishing.
              </p>
            </div>
          )}

          {/* Title */}
          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Title *
              <input
                className={`${INPUT_CLS} mt-1`}
                placeholder="e.g. Photosynthesis in Plants"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
              />
            </label>
          </div>

          {/* eBook Series */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              eBook Series
              <select
                className={`${INPUT_CLS} mt-1`}
                value={form.examTypeId}
                onChange={(e) => set('examTypeId', e.target.value)}
              >
                <option value="">Other (no series)</option>
                {seriesTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.code || t.description || `Series ${t.id}`}</option>
                ))}
              </select>
            </label>
          </div>

          {/* Subject */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Subject
              <select
                className={`${INPUT_CLS} mt-1`}
                value={form.subject}
                onChange={(e) => set('subject', e.target.value)}
              >
                <option value="">Select subject</option>
                {seriesSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          {/* Class Level */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Class Level
              <select
                className={`${INPUT_CLS} mt-1`}
                value={form.classLevel}
                onChange={(e) => set('classLevel', e.target.value)}
              >
                <option value="">Select class</option>
                {CLASS_LEVELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>

          {/* Language */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Language
              <select
                className={`${INPUT_CLS} mt-1`}
                value={form.language}
                onChange={(e) => set('language', e.target.value)}
              >
                {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
          </div>

          {/* Board */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Board
              <select
                className={`${INPUT_CLS} mt-1`}
                value={form.board}
                onChange={(e) => set('board', e.target.value)}
              >
                <option value="">Select board</option>
                {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
          </div>

          {/* Description */}
          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Description
              <textarea
                className={`${INPUT_CLS} resize-none mt-1`}
                rows={3}
                placeholder="Brief description…"
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </label>
          </div>

          {/* Short Description */}
          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Short Description
              <input
                className={`${INPUT_CLS} mt-1`}
                placeholder="Brief tagline…"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
              />
            </label>
          </div>

          {/* Content Tabs */}
          <div className="col-span-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Content</label>
            <div className="flex border-b border-gray-200 mb-2">
              {[['ebooks', 'E-Books'], ['courses', 'Courses'], ['assessments', 'Assessments'], ['chapters', 'Chapters']].map(([key, label]) => (
                <button key={key} type="button" onClick={() => setContentTab(key)}
                  className={`px-4 py-2 text-[13px] font-medium cursor-pointer bg-transparent border-0 border-b-2 -mb-px transition-colors ${contentTab === key ? 'text-indigo-600 border-indigo-600' : 'text-gray-500 border-transparent'}`}>
                  {label}
                </button>
              ))}
            </div>
            <input className={`${INPUT_CLS} mb-2`}
              placeholder={contentTab === 'chapters' ? 'Search assessment chapters...' : `Search ${contentTab}...`}
              value={SEARCH_OF[contentTab].value}
              onChange={(e) => SEARCH_OF[contentTab].set(e.target.value)}
            />
            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
              {contentLoading ? <div className="flex justify-center py-6"><CircularProgress size={20} /></div> : (() => {
                const needle = SEARCH_OF[contentTab].value.toLowerCase();
                const { list: selected, set: setSelected } = SELECTION_OF[contentTab];
                const isChapters = contentTab === 'chapters';

                const source = { ebooks, courses, assessments, chapters }[contentTab];
                const items = source.filter((i) => (
                  isChapters
                    // Chapter names repeat across sources ("M1" exists under
                    // several), so the source title is searchable too.
                    ? `${i.chapterName || ''} ${i.sourceTitle || ''}`.toLowerCase().includes(needle)
                    : (i.title || '').toLowerCase().includes(needle)
                ));

                if (items.length === 0) {
                  return (
                    <p className="text-center text-xs text-gray-400 py-6">
                      {isChapters && chapters.length === 0
                        ? 'No assessment chapters yet. Create them under Assessments first.'
                        : 'No items found'}
                    </p>
                  );
                }
                return items.map((item) => (
                  <div key={item.id} onClick={() => toggleId(selected, setSelected, item.id)}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                    <Checkbox size="small" checked={selected.includes(item.id)}
                      onChange={() => toggleId(selected, setSelected, item.id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: '4px', mr: '8px', color: '#d1d5db', '&.Mui-checked': { color: '#4f46e5' } }} />
                    {isChapters ? (
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm text-gray-800 truncate">{item.chapterName}</span>
                        <span className="text-[11px] text-gray-400 truncate">
                          {item.sourceTitle || 'No source'} · {item.questionCount || 0} questions
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-800">{item.title || item.name}</span>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cover Image</label>
            <CoverImageUpload
              existingUrl={null}
              coverImage={coverImage}
              preview={coverImgPreview}
              onFileChange={handleFileChange}
              onRemove={() => { setCoverImage(null); setCoverImgPreview(null); setCoverImageError(''); }}
              error={coverImageError}
            />
          </div>

          {error && <p className="col-span-2 text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex gap-3 px-6 pb-5 shrink-0 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={() => { setStep('mode'); setError(''); }}
            className="flex-1 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || (mode === 'upload' && !docFile)}
            className="flex-1 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {(() => {
              if (!saving) return mode === 'upload' ? 'Create & Import' : 'Create & Edit';
              // In upload mode the wait is mostly the file going up, so show it.
              // Once the bytes are up, the server is parsing and the wait is real.
              if (mode === 'upload') return uploadPct > 0 && uploadPct < 100 ? `Uploading ${uploadPct}%…` : 'Importing…';
              return 'Creating…';
            })()}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   List page
   ═════════════════════════════════════════════════════════════════════════ */

// Every filter the list understands, in one place. The URL is the single source
// of truth for all of them, so a filtered list is a shareable link and the back
// button walks back through the filters the way a user expects.
//
// `param` is the query-string key, `api` is the key sent to the API. Adding a
// filter means adding one row here plus one control in the filter bar.
const FILTERS = [
  { param: 'subject',    api: 'subject',    label: 'Subject',  options: SUBJECTS },
  { param: 'class',      api: 'classLevel', label: 'Class',    options: CLASS_LEVELS },
  { param: 'board',      api: 'board',      label: 'Board',    options: BOARDS },
  { param: 'language',   api: 'language',   label: 'Language', options: LANGUAGES },
];

const STATUS_TABS = [
  { value: '',          label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft',     label: 'Drafts' },
];

// Encoded in the URL as a single `sort` key so the query string stays readable.
const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first',    sortBy: 'createdAt', sortOrder: 'desc' },
  { value: 'createdAt:asc',  label: 'Oldest first',    sortBy: 'createdAt', sortOrder: 'asc'  },
  { value: 'updatedAt:desc', label: 'Recently updated',sortBy: 'updatedAt', sortOrder: 'desc' },
  { value: 'title:asc',      label: 'Title A–Z',       sortBy: 'title',     sortOrder: 'asc'  },
  { value: 'title:desc',     label: 'Title Z–A',       sortBy: 'title',     sortOrder: 'desc' },
];
const DEFAULT_SORT = 'createdAt:desc';

const PAGE_SIZES = [10, 25, 50, 100];

const SELECT_CLS =
  'w-full appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm text-gray-700 ' +
  'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 cursor-pointer';

// The API has been returning a few different envelopes over time (a bare array,
// { rows, count }, { data, totalCount }). Normalising here keeps the component
// from caring which one it gets today.
function normalizeEbookResponse(result) {
  const payload = result?.data;
  const rows = Array.isArray(payload)
    ? payload
    : payload?.rows || payload?.data || payload?.ebooks || [];
  const total =
    payload?.totalCount ?? payload?.total ?? payload?.count ??
    (Array.isArray(payload) ? payload.length : rows.length);
  return { rows: Array.isArray(rows) ? rows : [], total: Number(total) || 0 };
}

const coverOf = (row) =>
  row?.coverImage || row?.coverImageUrl || row?.cover_image || row?.thumbnail || null;

function FilterSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="relative">
        <select className={SELECT_CLS} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-xs font-medium text-indigo-700">
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="p-0.5 rounded-full hover:bg-indigo-100 text-indigo-500 hover:text-indigo-800"
        aria-label="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

const EBooksList = () => {
  const navigate = useNavigate();
  const menuRefs = useRef({});
  const [searchParams, setSearchParams] = useSearchParams();

  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [maxCount, setMaxCount] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEbook, setSelectedEbook] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // ── URL is the source of truth for everything the query depends on ────────
  const urlSearch = searchParams.get('q') || '';
  const status    = searchParams.get('status') || '';
  const sort      = searchParams.get('sort') || DEFAULT_SORT;
  const page      = Math.max(1, Number(searchParams.get('page')) || 1);
  const size      = PAGE_SIZES.includes(Number(searchParams.get('size')))
    ? Number(searchParams.get('size'))
    : 10;

  const getFilter = (param) => searchParams.get(param) || '';

  const activeFilters = useMemo(
    () => FILTERS.map((f) => ({ ...f, value: searchParams.get(f.param) || '' })),
    [searchParams],
  );

  // The text box types faster than the URL should change, so it keeps its own
  // state and pushes the debounced value into the query string.
  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebounce(searchInput, 450);

  const [showAdvanced, setShowAdvanced] = useState(
    () => FILTERS.some((f) => searchParams.get(f.param)),
  );

  const patchParams = useCallback((patch, { resetPage = true } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, String(v));
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounced search → URL. Guarded so it does not fight a back-button change.
  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    patchParams({ q: debouncedSearch });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back/forward navigation changes the URL underneath us — mirror it back into
  // the input so the box always shows what the list is actually filtered by.
  useEffect(() => {
    setSearchInput((current) => (current === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  const filterKey = activeFilters.map((f) => f.value).join('|');

  const fetchEbooks = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const sortDef = SORT_OPTIONS.find((s) => s.value === sort) || SORT_OPTIONS[0];

      // The complete payload for GET /e-books/ebooks. Every filter the page can
      // apply is listed here explicitly so the request is easy to read in the
      // network tab. adminServices.getEbooks drops the empty ones before it
      // builds the query string, so the API never receives `subject=`.
      const params = {
        search:     urlSearch,
        page,
        size,
        limit:      size,                       // some list endpoints read `limit`
        sortBy:     sortDef.sortBy,             // createdAt | updatedAt | title
        sortOrder:  sortDef.sortOrder,          // asc | desc
        status,                                 // '' | 'published' | 'draft'
        // Boolean twin of `status`, for a `where: { isPublished }` style query.
        // Left undefined when the status tab is "All" so it is not sent at all.
        isPublished: status ? status === 'published' : undefined,
        subject:    getFilter('subject'),
        classLevel: getFilter('class'),
        board:      getFilter('board'),
        language:   getFilter('language'),
      };

      const result = await adminServices.getEbooks(params);

      if (result && result.ok === false) {
        throw new Error(result?.data?.message || 'Request failed');
      }
      const { rows, total } = normalizeEbookResponse(result);
      setEbooks(rows);
      setMaxCount(total);
    } catch (err) {
      console.error('Error fetching ebooks:', err);
      setEbooks([]);
      setMaxCount(0);
      setFetchError(err?.message || 'We could not load your eBooks.');
    } finally {
      setLoading(false);
    }
  }, [urlSearch, page, size, status, sort, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchEbooks(); }, [fetchEbooks]);

  // ── Action menu dismissal ─────────────────────────────────────────────────
  useEffect(() => {
    if (!openActionMenuId) return undefined;
    const handleClickOutside = (e) => {
      const menu = menuRefs.current[openActionMenuId];
      const btn  = menuRefs.current[`btn_${openActionMenuId}`];
      if (menu?.contains(e.target) || btn?.contains(e.target)) return;
      setOpenActionMenuId(null);
    };
    const close = () => setOpenActionMenuId(null);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openActionMenuId]);

  const notify = (msg) => { setSnackbarTitle(msg); setOpenSnackbar(true); };

  const handleDelete = async () => {
    if (!selectedEbook) return;
    try {
      await adminServices.deleteEbook(selectedEbook.id);
      notify('eBook deleted successfully');
      setDeleteModalOpen(false);
      setSelectedEbook(null);
      // Deleting the only row on the last page would leave an empty page.
      if (ebooks.length === 1 && page > 1) patchParams({ page: page - 1 }, { resetPage: false });
      else fetchEbooks();
    } catch (err) {
      notify('Error deleting eBook');
    }
  };

  const handlePublishToggle = async (ebook, isPublished) => {
    setBusyId(ebook.id);
    try {
      await adminServices.publishEbook(ebook.id, isPublished);
      notify(isPublished ? 'eBook published' : 'eBook unpublished');
      fetchEbooks();
    } catch (err) {
      notify('Error updating publish status');
    } finally {
      setBusyId(null);
    }
  };

  // ── Filter helpers ────────────────────────────────────────────────────────
  const appliedChips = useMemo(() => {
    const chips = [];
    if (urlSearch) chips.push({ key: 'q', label: `“${urlSearch}”` });
    if (status) {
      chips.push({ key: 'status', label: STATUS_TABS.find((s) => s.value === status)?.label || status });
    }
    activeFilters.forEach((f) => {
      if (f.value) chips.push({ key: f.param, label: `${f.label}: ${f.value}` });
    });
    return chips;
  }, [urlSearch, status, activeFilters]);

  const hasFilters = appliedChips.length > 0 || sort !== DEFAULT_SORT;

  const clearAll = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const removeChip = (key) => {
    if (key === 'q') setSearchInput('');
    patchParams({ [key]: '' });
  };

  // ── Table ─────────────────────────────────────────────────────────────────
  const statusBadge = (isPublished) => (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isPublished ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      {isPublished ? 'Published' : 'Draft'}
    </span>
  );

  const columns = [
    {
      label: 'eBook',
      key: 'title',
      render: (row) => {
        const cover = coverOf(row);
        const desc = row.shortDescription || row.description;
        // eBooks tab showed "Unknown author": the API returns the creator's name parts separately, so the full name is composed here.
        const authorName = [row.authorFirstName, row.authorLastName].filter(Boolean).join(' ').trim();
        return (
          <div className="flex items-start gap-3 min-w-0">
            <div className="shrink-0 w-10 h-[52px] rounded-md overflow-hidden bg-indigo-50 ring-1 ring-gray-200 flex items-center justify-center">
              {cover
                ? <img src={cover} alt="" className="w-full h-full object-cover"
                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <BookOpen className="w-4 h-4 text-indigo-400" />}
            </div>
            <div className="flex flex-col min-w-0">
              <button
                type="button"
                onClick={() => navigate(`/app/ebook-creator/${row.id}`)}
                className="text-sm font-semibold text-gray-900 hover:text-indigo-600 text-left truncate max-w-[22rem] bg-transparent border-0 p-0 cursor-pointer"
                title={row.title || ''}
              >
                {row.title || 'Untitled eBook'}
              </button>
              {desc && (
                <span className="text-xs text-gray-500 mt-0.5 truncate max-w-[22rem]" title={desc}>
                  {desc}
                </span>
              )}
              <span className="text-[11px] text-gray-400 mt-1 truncate max-w-[22rem]" title={authorName}>
                {authorName || 'Unknown author'}
              </span>
              <span className="text-[11px] text-gray-400 truncate max-w-[22rem]" title={row.authorEmail || ''}>
                {row.authorEmail ? `${row.authorEmail} · ` : ''}
                {row.createdAt ? moment(row.createdAt).format('DD MMM YYYY') : '—'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      label: 'Subject',
      key: 'subjectId',
      render: (row) => (
        row.subjectId || row.subject
          ? <span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-gray-700">
              {row.subjectId || row.subject}
            </span>
          : <span className="text-sm text-gray-300">—</span>
      ),
    },
    {
      label: 'Class',
      key: 'classLevel',
      render: (row) => <span className="text-sm text-gray-700">{row.classLevel || '—'}</span>,
    },
    {
      label: 'Chapters',
      key: 'chapterCount',
      render: (row) => (
        <span className="text-sm font-medium text-gray-800 tabular-nums">{row.chapterCount ?? 0}</span>
      ),
    },
    {
      label: 'Status',
      key: 'isPublished',
      render: (row) => statusBadge(row.isPublished),
    },
    {
      label: 'Updated',
      key: 'updatedAt',
      render: (row) => (
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {row.updatedAt || row.createdAt ? moment(row.updatedAt || row.createdAt).fromNow() : '—'}
        </span>
      ),
    },
    {
      label: '',
      key: 'actions',
      render: (row) => (
        <div className="relative flex items-center justify-end">
          {busyId === row.id ? (
            <CircularProgress size={18} sx={{ color: '#6366f1' }} />
          ) : (
            <button
              ref={(el) => { menuRefs.current[`btn_${row.id}`] = el; }}
              type="button"
              aria-label="Row actions"
              onClick={() => {
                if (openActionMenuId === row.id) { setOpenActionMenuId(null); return; }
                const btn = menuRefs.current[`btn_${row.id}`];
                if (btn) {
                  const rect = btn.getBoundingClientRect();
                  // Fixed positioning: no scroll offset maths, and the menu
                  // flips above the button when it would fall off screen.
                  const flip = window.innerHeight - rect.bottom < 190;
                  setMenuPosition({
                    top: flip ? rect.top - 8 : rect.bottom + 6,
                    right: window.innerWidth - rect.right,
                    flip,
                  });
                }
                setOpenActionMenuId(row.id);
              }}
              className={`p-2 rounded-lg transition-colors ${
                openActionMenuId === row.id
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(maxCount / size));
  const from = maxCount === 0 ? 0 : (page - 1) * size + 1;
  const to   = Math.min(page * size, maxCount);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    if (page <= 3) [2, 3, 4].forEach((p) => set.add(p));
    if (page >= totalPages - 2) [totalPages - 3, totalPages - 2, totalPages - 1].forEach((p) => set.add(p));
    const list = [...set].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    list.forEach((p, i) => {
      if (i > 0 && p - list[i - 1] > 1) out.push(`gap-${p}`);
      out.push(p);
    });
    return out;
  }, [page, totalPages]);

  const renderEmptyState = () => {
    if (fetchError) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-sm">{fetchError}</p>
          <button
            type="button"
            onClick={fetchEbooks}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </div>
      );
    }
    // "Nothing matches" is a different problem from "nothing exists yet", and
    // needs a different button.
    if (hasFilters) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Search className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">No eBooks match these filters</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-sm">
            Try a different search term, or clear the filters to see everything.
          </p>
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <RotateCcw className="w-4 h-4" /> Clear filters
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
          <BookOpen className="w-7 h-7 text-indigo-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">No eBooks yet</h3>
        <p className="text-sm text-gray-500 mb-5 max-w-sm">
          Create one from scratch, or upload a Word document and we&apos;ll build the chapters for you.
        </p>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
        >
          <PlusCircle className="w-5 h-5" /> Create eBook
        </button>
      </div>
    );
  };

  const advancedCount = activeFilters.filter((f) => f.value).length;

  return (
    <Page title="eBooks">
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1600px] mx-auto">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">eBooks</h2>
            <p className="mt-1 text-sm text-gray-500">
              {loading
                ? 'Loading your library…'
                : `${maxCount} ${maxCount === 1 ? 'eBook' : 'eBooks'}${hasFilters ? ' matching your filters' : ' in your library'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
          >
            <PlusCircle className="w-5 h-5" /> Create eBook
          </button>
        </div>

        {/* ── Filter bar ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 mb-5">
          <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">

            {/* Search */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by title, subject or author…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Status segmented control */}
            <div className="flex items-center p-1 bg-gray-100 rounded-lg shrink-0">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.value || 'all'}
                  type="button"
                  onClick={() => patchParams({ status: t.value })}
                  className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all ${
                    status === t.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-800'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="relative shrink-0">
              <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={sort}
                onChange={(e) => patchParams({ sort: e.target.value === DEFAULT_SORT ? '' : e.target.value })}
                className="appearance-none bg-white border border-gray-300 rounded-lg pl-9 pr-8 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            {/* Advanced toggle */}
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-sm font-medium border transition-colors shrink-0 ${
                showAdvanced || advancedCount
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {advancedCount > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-bold leading-none">
                  {advancedCount}
                </span>
              )}
            </button>
          </div>

          {/* Advanced filters */}
          {showAdvanced && (
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 border-t border-gray-100 pt-4">
              {activeFilters.map((f) => (
                <FilterSelect
                  key={f.param}
                  label={f.label}
                  value={f.value}
                  options={f.options}
                  placeholder={`All ${f.label.toLowerCase()}s`}
                  onChange={(v) => patchParams({ [f.param]: v })}
                />
              ))}
            </div>
          )}

          {/* Applied chips */}
          {appliedChips.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500 mr-1">Active:</span>
              {appliedChips.map((c) => (
                <Chip key={c.key} onRemove={() => removeChip(c.key)}>{c.label}</Chip>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="ml-auto text-xs font-semibold text-indigo-600 hover:text-indigo-800"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* ── Table ──────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-visible">
          <ResponsiveTable
            columns={columns}
            data={ebooks}
            loading={loading}
            emptyStateComponent={renderEmptyState}
          />

          {/* Pagination */}
          {(maxCount > 0 || page > 1) && (
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">{from}</span>–
                    <span className="font-medium text-gray-900">{to}</span> of{' '}
                    <span className="font-medium text-gray-900">{maxCount}</span>
                  </p>
                  <span className="h-4 w-px bg-gray-200 hidden sm:block" />
                  <div className="hidden sm:flex items-center gap-2">
                    <span className="text-sm text-gray-500">Rows</span>
                    <select
                      value={size}
                      onChange={(e) => patchParams({ size: e.target.value })}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                    >
                      {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => patchParams({ page: page - 1 }, { resetPage: false })}
                    disabled={page <= 1 || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="hidden sm:flex items-center gap-1 mx-1">
                    {pageNumbers.map((p) => (
                      typeof p === 'string' ? (
                        <span key={p} className="px-1.5 text-sm text-gray-400">…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => patchParams({ page: p }, { resetPage: false })}
                          className={`min-w-[2.25rem] px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            page === p
                              ? 'bg-indigo-600 text-white'
                              : 'text-gray-700 hover:bg-gray-100'}`}
                        >
                          {p}
                        </button>
                      )
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => patchParams({ page: page + 1 }, { resetPage: false })}
                    disabled={page >= totalPages || loading}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Action dropdown portal ───────────────────────────────────────── */}
      {openActionMenuId && (() => {
        const row = ebooks.find((e) => e.id === openActionMenuId);
        if (!row) return null;
        return ReactDOM.createPortal(
          <div
            ref={(el) => { menuRefs.current[openActionMenuId] = el; }}
            style={{
              position: 'fixed',
              top: menuPosition.flip ? undefined : menuPosition.top,
              bottom: menuPosition.flip ? window.innerHeight - menuPosition.top : undefined,
              right: menuPosition.right,
              zIndex: 9999,
            }}
            className="w-48 rounded-xl border border-gray-200 bg-white shadow-xl py-1.5 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => { navigate(`/app/ebook-creator/${row.id}`); setOpenActionMenuId(null); }}
              className="w-full px-3.5 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
            >
              <Pencil className="w-4 h-4 text-gray-400" /> Edit
            </button>
            <button
              type="button"
              onClick={() => { handlePublishToggle(row, !row.isPublished); setOpenActionMenuId(null); }}
              className="w-full px-3.5 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
            >
              {row.isPublished
                ? <><Eye className="w-4 h-4 text-gray-400" /> Unpublish</>
                : <><Upload className="w-4 h-4 text-gray-400" /> Publish</>}
            </button>
            {!row.isPublished && (
              <>
                <div className="my-1 h-px bg-gray-100" />
                <button
                  type="button"
                  onClick={() => { setSelectedEbook(row); setDeleteModalOpen(true); setOpenActionMenuId(null); }}
                  className="w-full px-3.5 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </>
            )}
          </div>,
          document.body,
        );
      })()}

      <SnackBar open={openSnackbar} snackbarTitle={snackbarTitle} close={() => setOpenSnackbar(false)} />

      {deleteModalOpen && (
        <DeleteAlert
          open={deleteModalOpen}
          close={() => { setDeleteModalOpen(false); setSelectedEbook(null); }}
          confirm={handleDelete}
          title="Delete eBook"
          subTitle={`Are you sure you want to delete “${selectedEbook?.title || 'this eBook'}”? This action cannot be undone.`}
        />
      )}

      {showCreateModal && (
        <CreateEbookModal
          onClose={() => {
            setShowCreateModal(false);
            // An upload creates the eBook before the dialog closes, so the list
            // is stale by the time the author dismisses it.
            fetchEbooks();
          }}
          onCreated={(newId) => {
            setShowCreateModal(false);
            navigate(`/app/ebook-creator/${newId}`);
          }}
        />
      )}
    </Page>
  );
};

export default EBooksList;